import { logger } from "@/lib/logger";
import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Settings, 
  Save,
  TrendingUp,
  DollarSign,
  Zap,
  RefreshCw,
  Clock,
  CreditCard,
  Sparkles,
  Brain,
  Loader2
} from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { PlatformConnections } from "@/components/PlatformConnections";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SettingsPage({ user }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [settings, setSettings] = useState(null);
  const [advancedSettings, setAdvancedSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingAdvanced, setSavingAdvanced] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [hasAdvancedChanges, setHasAdvancedChanges] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // Handle OAuth callback results
  useEffect(() => {
    const oauthSuccess = searchParams.get("oauth_success");
    const oauthError = searchParams.get("oauth_error");
    const platform = searchParams.get("platform");
    const account = searchParams.get("account");

    if (oauthSuccess === "true" && platform) {
      toast.success(`Successfully connected to ${account || platform}!`);
      // Clear the URL params
      setSearchParams({});
    } else if (oauthError && platform) {
      toast.error(`Failed to connect ${platform}: ${oauthError.replace(/\+/g, ' ')}`);
      // Clear the URL params
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await fetch(`${API}/settings`, {
        credentials: "include",
      });
      if (response.ok) {
        setSettings(await response.json());
      }
    } catch (error) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAdvancedSettings = useCallback(async () => {
    try {
      const response = await fetch(`${API}/settings/advanced`, {
        credentials: "include",
      });
      if (response.ok) {
        setAdvancedSettings(await response.json());
      }
    } catch (error) {
      logger.error("Failed to load advanced settings");
    }
  }, []);

  useEffect(() => {
    fetchSettings();
    fetchAdvancedSettings();
  }, [fetchSettings, fetchAdvancedSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          roas_threshold: settings.roas_threshold,
          auto_optimize: settings.auto_optimize,
          min_budget: settings.min_budget,
          max_budget: settings.max_budget,
        }),
      });

      if (response.ok) {
        toast.success("Settings saved");
        setHasChanges(false);
      } else {
        toast.error("Failed to save settings");
      }
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAdvanced = async () => {
    setSavingAdvanced(true);
    try {
      const response = await fetch(`${API}/settings/advanced`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          auto_topup: advancedSettings.auto_topup,
          scheduled_sync: advancedSettings.scheduled_sync,
          ai_auto_optimize: advancedSettings.ai_auto_optimize,
          ai_optimize_frequency: advancedSettings.ai_optimize_frequency,
        }),
      });

      if (response.ok) {
        toast.success("Advanced settings saved");
        setHasAdvancedChanges(false);
      } else {
        toast.error("Failed to save advanced settings");
      }
    } catch (error) {
      toast.error("Failed to save advanced settings");
    } finally {
      setSavingAdvanced(false);
    }
  };

  const handleManualSync = async () => {
    setSyncing(true);
    try {
      const response = await fetch(`${API}/sync/run`, {
        method: "POST",
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
      } else {
        toast.error("Sync failed");
      }
    } catch (error) {
      toast.error("Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings({ ...settings, [key]: value });
    setHasChanges(true);
  };

  const updateAdvancedSetting = (path, value) => {
    const keys = path.split(".");
    const newSettings = { ...advancedSettings };
    let current = newSettings;
    
    for (let i = 0; i < keys.length - 1; i++) {
      current[keys[i]] = { ...current[keys[i]] };
      current = current[keys[i]];
    }
    current[keys[keys.length - 1]] = value;
    
    setAdvancedSettings(newSettings);
    setHasAdvancedChanges(true);
  };

  if (loading) {
    return (
      <DashboardLayout user={user}>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="spinner" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout user={user}>
      <div data-testid="settings-page" className="space-y-6 max-w-3xl">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl lg:text-4xl text-white font-bold mb-2">
              Settings
            </h1>
            <p className="text-zinc-400">
              Configure your optimization and automation preferences
            </p>
          </div>
          <div className="flex gap-2">
            {hasAdvancedChanges && (
              <Button
                onClick={handleSaveAdvanced}
                disabled={savingAdvanced}
                className="bg-violet-600 text-white hover:bg-violet-700 font-semibold rounded-lg px-4 py-2.5"
              >
                {savingAdvanced ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Advanced
              </Button>
            )}
            <Button
              data-testid="save-settings-btn"
              onClick={handleSave}
              disabled={!hasChanges || saving}
              className={`${hasChanges ? "bg-white text-black hover:bg-zinc-200" : "bg-white/10 text-zinc-400"} font-semibold rounded-lg px-6 py-2.5 transition-all`}
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Changes
            </Button>
          </div>
        </div>


        {/* Auto-Optimization */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
        >
          <Card className="bg-[#121212] border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-400" />
                Auto-Optimization
              </CardTitle>
              <CardDescription className="text-zinc-400">
                Enable automatic budget adjustments based on campaign performance
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-white">Enable Auto-Optimization</Label>
                  <p className="text-zinc-400 text-sm mt-1">
                    Automatically adjust budgets when ROAS is above or below threshold
                  </p>
                </div>
                <Switch
                  data-testid="auto-optimize-toggle"
                  checked={settings?.auto_optimize}
                  onCheckedChange={(checked) => updateSetting("auto_optimize", checked)}
                />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* AI-Driven Budget Reallocation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
        >
          <Card className="bg-[#121212] border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Brain className="w-5 h-5 text-violet-400" />
                AI Budget Reallocation
                <Badge className="bg-violet-500/20 text-violet-300 border-violet-500/30">AI</Badge>
              </CardTitle>
              <CardDescription className="text-zinc-400">
                Let AI automatically reallocate budgets to best-performing campaigns
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-white">Enable AI Auto-Reallocation</Label>
                  <p className="text-zinc-400 text-sm mt-1">
                    AI will shift budgets based on ROAS, CTR, and conversion rates
                  </p>
                </div>
                <Switch
                  checked={advancedSettings?.ai_auto_optimize}
                  onCheckedChange={(checked) => updateAdvancedSetting("ai_auto_optimize", checked)}
                />
              </div>
              
              {advancedSettings?.ai_auto_optimize && (
                <div className="space-y-2 pt-2">
                  <Label className="text-zinc-400">Reallocation Frequency</Label>
                  <Select
                    value={advancedSettings?.ai_optimize_frequency || "daily"}
                    onValueChange={(value) => updateAdvancedSetting("ai_optimize_frequency", value)}
                  >
                    <SelectTrigger className="bg-black/50 border-white/10 text-white w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#18181B] border-white/10">
                      <SelectItem value="daily" className="text-white hover:bg-white/10">Daily</SelectItem>
                      <SelectItem value="weekly" className="text-white hover:bg-white/10">Weekly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* ROAS Threshold */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="bg-[#121212] border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-emerald-400" />
                ROAS Threshold
              </CardTitle>
              <CardDescription className="text-zinc-400">
                Campaigns below this threshold will have budget decreased
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <div className="flex items-center justify-between mb-4">
                  <Label className="text-white">Target ROAS</Label>
                  <span className="text-2xl font-heading font-bold text-white tabular-nums">
                    {settings?.roas_threshold?.toFixed(2)}x
                  </span>
                </div>
                <Slider
                  data-testid="roas-threshold-slider"
                  value={[settings?.roas_threshold || 1.75]}
                  onValueChange={(value) => updateSetting("roas_threshold", value[0])}
                  min={1}
                  max={5}
                  step={0.05}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-zinc-400 mt-2">
                  <span>1.00x</span>
                  <span>5.00x</span>
                </div>
              </div>

              <div className="p-4 rounded-lg bg-white/5 border border-white/5">
                <h4 className="text-white font-medium mb-2">How it works:</h4>
                <ul className="text-zinc-400 text-sm space-y-1">
                  <li>• ROAS &lt; {settings?.roas_threshold?.toFixed(2)}x → Budget decreased by 20%</li>
                  <li>• ROAS ≥ {(settings?.roas_threshold * 1.5)?.toFixed(2)}x → Budget increased by 25%</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Budget Limits */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
        >
          <Card className="bg-[#121212] border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-blue-400" />
                Budget Limits
              </CardTitle>
              <CardDescription className="text-zinc-400">
                Set minimum and maximum budget constraints for optimization
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-zinc-400">Minimum Daily Budget ($)</Label>
                  <Input
                    data-testid="min-budget-input"
                    type="number"
                    value={settings?.min_budget}
                    onChange={(e) => updateSetting("min_budget", parseFloat(e.target.value))}
                    className="bg-black/50 border-white/10 text-white"
                  />
                  <p className="text-zinc-500 text-xs">
                    Budgets will not be reduced below this amount
                  </p>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-400">Maximum Daily Budget ($)</Label>
                  <Input
                    data-testid="max-budget-input"
                    type="number"
                    value={settings?.max_budget}
                    onChange={(e) => updateSetting("max_budget", parseFloat(e.target.value))}
                    className="bg-black/50 border-white/10 text-white"
                  />
                  <p className="text-zinc-500 text-xs">
                    Budgets will not be increased above this amount
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Auto Top-Up */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="bg-[#121212] border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-emerald-400" />
                Automatic Budget Top-Up
              </CardTitle>
              <CardDescription className="text-zinc-400">
                Automatically add funds when campaign budgets run low
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-white">Enable Auto Top-Up</Label>
                  <p className="text-zinc-400 text-sm mt-1">
                    Automatically charge your card when budget falls below threshold
                  </p>
                </div>
                <Switch
                  checked={advancedSettings?.auto_topup?.enabled}
                  onCheckedChange={(checked) => updateAdvancedSetting("auto_topup.enabled", checked)}
                />
              </div>
              
              {advancedSettings?.auto_topup?.enabled && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
                  <div className="space-y-2">
                    <Label className="text-zinc-400">Trigger Threshold ($)</Label>
                    <Input
                      type="number"
                      value={advancedSettings?.auto_topup?.threshold}
                      onChange={(e) => updateAdvancedSetting("auto_topup.threshold", parseFloat(e.target.value))}
                      className="bg-black/50 border-white/10 text-white"
                    />
                    <p className="text-zinc-500 text-xs">Top-up when budget falls below</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-400">Top-Up Amount ($)</Label>
                    <Input
                      type="number"
                      value={advancedSettings?.auto_topup?.top_up_amount}
                      onChange={(e) => updateAdvancedSetting("auto_topup.top_up_amount", parseFloat(e.target.value))}
                      className="bg-black/50 border-white/10 text-white"
                    />
                    <p className="text-zinc-500 text-xs">Amount to add each time</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-400">Monthly Limit ($)</Label>
                    <Input
                      type="number"
                      value={advancedSettings?.auto_topup?.max_monthly_topup}
                      onChange={(e) => updateAdvancedSetting("auto_topup.max_monthly_topup", parseFloat(e.target.value))}
                      className="bg-black/50 border-white/10 text-white"
                    />
                    <p className="text-zinc-500 text-xs">Maximum monthly auto-charges</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Scheduled Sync */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
        >
          <Card className="bg-[#121212] border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Clock className="w-5 h-5 text-blue-400" />
                Scheduled Data Sync
              </CardTitle>
              <CardDescription className="text-zinc-400">
                Automatically sync campaign data from connected ad platforms
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-white">Enable Scheduled Sync</Label>
                  <p className="text-zinc-400 text-sm mt-1">
                    Keep your campaign data up-to-date automatically
                  </p>
                </div>
                <Switch
                  checked={advancedSettings?.scheduled_sync?.enabled}
                  onCheckedChange={(checked) => updateAdvancedSetting("scheduled_sync.enabled", checked)}
                />
              </div>
              
              {advancedSettings?.scheduled_sync?.enabled && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                  <div className="space-y-2">
                    <Label className="text-zinc-400">Sync Frequency</Label>
                    <Select
                      value={advancedSettings?.scheduled_sync?.frequency || "daily"}
                      onValueChange={(value) => updateAdvancedSetting("scheduled_sync.frequency", value)}
                    >
                      <SelectTrigger className="bg-black/50 border-white/10 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#18181B] border-white/10">
                        <SelectItem value="hourly" className="text-white hover:bg-white/10">Every Hour</SelectItem>
                        <SelectItem value="daily" className="text-white hover:bg-white/10">Daily</SelectItem>
                        <SelectItem value="weekly" className="text-white hover:bg-white/10">Weekly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-400">Sync Time (24h)</Label>
                    <Input
                      type="time"
                      value={advancedSettings?.scheduled_sync?.sync_time || "06:00"}
                      onChange={(e) => updateAdvancedSetting("scheduled_sync.sync_time", e.target.value)}
                      className="bg-black/50 border-white/10 text-white"
                    />
                  </div>
                </div>
              )}
              
              <div className="pt-2">
                <Button
                  variant="outline"
                  onClick={handleManualSync}
                  disabled={syncing}
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  {syncing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  Sync Now
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Account */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="bg-[#121212] border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Settings className="w-5 h-5 text-zinc-400" />
                Account
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center py-3 border-b border-white/5">
                  <span className="text-zinc-400">Email</span>
                  <span className="text-white">{user?.email}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-white/5">
                  <span className="text-zinc-400">Name</span>
                  <span className="text-white">{user?.name}</span>
                </div>
                <div className="flex justify-between items-center py-3">
                  <span className="text-zinc-400">User ID</span>
                  <span className="text-zinc-400 text-sm font-mono">{user?.user_id}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </DashboardLayout>
  );
}
